/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {GroupCategoryEntity} from '@ion/core/src/types/entities';
import EntityPropertyChooser, {
  EntityPropertyChooserClientProps,
  GroupPropertyKind
} from './EntityPropertyChooser';

// extended composed props and expose them as a convenience to the client ...
export interface EntityCategoryChooserProps extends EntityPropertyChooserClientProps {
  entity: GroupCategoryEntity
};

/**
 * @EntityCategoryChooser
 *
 * The EntityCategoryChooser provides a list of user defined, account specific entity
 * categories for display within a dropdown list, allowing the user to select a category
 * from the list for subsequent persistence to the entity.
 *
 * ```typescript
 *
 *     import {EntityCategoryChooser} from '@ion/dls/src/editors/';
 *     import {GroupCategoryEntity} from '@ion/core/src/types/entites';
 *
 *     // add an entity category chooser for assets ...
 *     <EntityCategoryChooser entity={GroupCategoryEntity.asset}
 *       defaultValue={asset.assetCategory}
 *       isLoading={isLoading}
 *       onChange(setCategory) />

 *     // add an entity category chooser for routes ...
 *     <EntityCategoryChooser entity={GroupCategoryEntity.routes}
 *       defaultValue={operator.operatorCategory}
 *       isLoading={isLoading}
 *       onChange(setCategory) />
 *
 *     // provide a list of categories (allows for bypassing the API ...)
 *     <EntityCategoryChooser entity={GroupCategoryEntity.asset}
 *       values={operatorcategories.value} />
 *
 *     // add a read-only entity category chooser for assets and operators ...
 *     <EntityCategoryChooser entity={GroupCategoryEntity.asset}
 *       defaultValue={operator.operatorCategory}
 *       readOnly />
 *     <EntityCategoryChooser entity={GroupCategoryEntity.operator}
 *       isLoading={isLoading}
 *       readOnly />
 *
 * ```
 *
 * @entity            Required, specifies the context in which the component is displaying
 *                    entity categories (e.g. "Asset Categoryies", "Route Categoryies").
 * @isLoading         Required, when true, indicates that the component is to display its
 *                    loading state, and when changed to false, indicates that the items
 *                    within the dropdown are to be updated to reflect the latest entity
 *                    categories from the GroupProperties API.
 * @defaultValue      Allows for providing a default selected value from the list.
 * @readOnly          When true, the component will be displayed in read-only mode;
 *                    that is, disabled. Defaults to false.
 * @onChange          Callback prop which will be invoked with the seleted entity category.
 * @values            Optional list of entity categories to be used. When provided, the
 *                    GroupProperties API will be bypassed and only the items provided
 *                    will be displayed.
 * @includeGroupName  When true, indicates that the account for which the entity categories
 *                    have been provided should be displayed within the label.
 * @classes           Optional classes to apply to the wrapping element.
 */
export const EntityCategoryChooser = (props: EntityPropertyChooserClientProps) => (
  <EntityPropertyChooser {...props}
    kind={GroupPropertyKind.Categories}
    translation='common:editors.entityCategory' />
);
export default EntityCategoryChooser;
